import { CampaignType } from "@/@types/campaign";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Filter } from "lucide-react";

interface CampaignTypeFilterProps {
  selectedTypes: CampaignType[];
  onTypesChange: (types: CampaignType[]) => void;
  countByType?: Record<string, number>;
}

export function CampaignTypeFilter({
  selectedTypes,
  onTypesChange,
  // countByType, // TODO: Use this to show counts per campaign type
}: CampaignTypeFilterProps) {
  const types = [
    { value: CampaignType.PRO012, label: "Combo Legado", color: "bg-gray-100 text-gray-700" },
    { value: CampaignType.PRO013, label: "Leve X Pague Y", color: "bg-purple-100 text-purple-700" },
    { value: CampaignType.PRO014, label: "Promoção Itens", color: "bg-blue-100 text-blue-700" },
    { value: CampaignType.PRO017, label: "Combo Moderno", color: "bg-green-100 text-green-700" },
  ];

  const handleToggle = (type: CampaignType) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];
    onTypesChange(newTypes);
  };

  const handleClearAll = () => {
    onTypesChange([]);
  };

  const handleSelectAll = () => {
    onTypesChange(types.map((t) => t.value));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <Filter className="mr-2 h-4 w-4" />
          Tipo de Campanha
          {selectedTypes.length > 0 && (
            <>
              <div className="mx-2 h-4 w-[1px] bg-gray-300" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedTypes.length}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedTypes.length > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedTypes.length} selecionados
                  </Badge>
                ) : (
                  types
                    .filter((type) => selectedTypes.includes(type.value))
                    .map((type) => (
                      <Badge
                        key={type.value}
                        variant="secondary"
                        className={`rounded-sm px-1 font-normal ${type.color}`}
                      >
                        {type.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Tipo de Campanha</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleSelectAll}
              >
                Todos
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleClearAll}
                disabled={selectedTypes.length === 0}
              >
                Limpar
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            {types.map((type) => {
              return (
                <div key={type.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`type-${type.value}`}
                    checked={selectedTypes.includes(type.value)}
                    onCheckedChange={() => handleToggle(type.value)}
                  />
                  <Label
                    htmlFor={`type-${type.value}`}
                    className="flex-1 text-sm font-normal cursor-pointer flex items-center justify-between"
                  >
                    <span>{type.label}</span>
                    <span className="text-xs text-muted-foreground/60 font-mono">{type.value}</span>
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
